/**
 * Icônes en SVG inline, colorées par `currentColor`.
 *
 * Le kit visuel fournit ces icônes en PNG, mais avec une tuile de fond et une
 * couleur cuites dans l'image : impossible de distinguer l'onglet actif de
 * l'inactif, et le fond clair reste clair sur un thème sombre. L'artifact du
 * système de design utilisait d'ailleurs lui-même du SVG inline pour sa
 * navigation. Les PNG restent la référence visuelle, pas les assets.
 */

type Props = { name: IconName; size?: number }

export type IconName =
  | 'home' | 'list' | 'wallet' | 'person' | 'plus'
  | 'food' | 'transport' | 'school' | 'ceremony' | 'health'
  | 'housing' | 'leisure' | 'savings' | 'other'
  | 'arrowUp' | 'arrowDown' | 'check' | 'close' | 'cloud' | 'cloudOff' | 'lock'
  | 'grip' | 'more' | 'trash' | 'pencil' | 'arrowLeft'
  | 'history' | 'ellipsis' | 'mail' | 'phone' | 'calendar' | 'refresh' | 'bell'
  | 'sun' | 'moon' | 'monitor' | 'chevronDown'

const PATHS: Record<IconName, string> = {
  home:      'M3 11.5 12 4l9 7.5M5.5 10v9.5h13V10',
  list:      'M4 7h16M4 12h16M4 17h10',
  wallet:    'M3.5 8.5h17v10h-17zM3.5 8.5V6h13v2.5M16.5 13.5h2',
  person:    'M12 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5',
  plus:      'M12 5.5v13M5.5 12h13',
  food:      'M7 4v7m0 0v9m0-9a2.5 2.5 0 0 0 0-5M17 4c-1.5 1.5-2 3-2 5s.7 2.5 2 2.5V20',
  transport: 'M4 16.5h16M6 16.5V9l2-4h8l2 4v7.5M7.5 19.5v-3M16.5 19.5v-3M6 12.5h12',
  school:    'M12 4 2.5 8.5 12 13l9.5-4.5L12 4ZM6 10.5V16c0 1.5 2.7 3 6 3s6-1.5 6-3v-5.5',
  ceremony:  'M12 4.5 13.9 9l4.6.4-3.5 3 1.1 4.5L12 14.5 7.9 16.9 9 12.4l-3.5-3L10.1 9 12 4.5Z',
  health:    'M12 20s-7-4.4-7-9.2A4 4 0 0 1 12 8a4 4 0 0 1 7 2.8C19 15.6 12 20 12 20Z',
  housing:   'M4 20V10l8-6 8 6v10M9.5 20v-5.5h5V20',
  leisure:   'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM9.5 10h.01M14.5 10h.01M9 14.5c1.8 1.4 4.2 1.4 6 0',
  savings:   'M6 18.5v2M17 18.5v2M4 12c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5-3.6 6.5-8 6.5S4 16 4 12ZM15.5 10.5h.01M8 6c-.5-1.5.4-2.6 1.8-2.6',
  other:     'M6.5 12h.01M12 12h.01M17.5 12h.01',
  arrowUp:   'M12 19V5M6 11l6-6 6 6',
  arrowDown: 'M12 5v14M6 13l6 6 6-6',
  check:     'M5 12.5 10 17.5 19 7',
  close:     'M6 6l12 12M18 6 6 18',
  cloud:     'M7 18.5h10a4 4 0 0 0 .4-8 6 6 0 0 0-11.6 1.4A3.6 3.6 0 0 0 7 18.5Z',
  cloudOff:  'M7 18.5h10a4 4 0 0 0 .4-8 6 6 0 0 0-11.6 1.4A3.6 3.6 0 0 0 7 18.5ZM4 4l16 16',
  lock:      'M5.5 10.5h13v9.5h-13zM8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3',
  // Deux colonnes de points : la poignée de déplacement universelle. Trois
  // traits horizontaux se lisaient comme un menu, pas comme une prise.
  grip:      'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  more:      'M12 6h.01M12 12h.01M12 18h.01',
  trash:     'M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 13h9l1-13M10 10.5v6M14 10.5v6',
  pencil:    'M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3Z',
  arrowLeft: 'M19 12H5M11 6l-6 6 6 6',
  // Une enveloppe fermée : le rabat en V dit « courrier » d'un seul trait.
  mail:      'M3.5 6.5h17v11h-17zM3.5 7l8.5 6 8.5-6',
  // Un combiné, réduit à sa courbe — le pictogramme le plus reconnu au monde.
  phone:     'M7.5 4.5 9.5 9l-2 1.5a11 11 0 0 0 6 6L15 14.5 19.5 16.5v3a1.5 1.5 0 0 1-1.7 1.5C10.4 20 4 13.6 3 6.2A1.5 1.5 0 0 1 4.5 4.5h3Z',
  // Un calendrier : deux anneaux, un cadre, une barre de titre.
  calendar:  'M4.5 6.5h15v13h-15zM4.5 10.5h15M8.5 4v4M15.5 4v4',
  // Trois points alignés : « il y en a d'autres ». Distinct de `more`, qui est
  // vertical et ouvre un menu — ici on prolonge une liste, on ne l'actionne pas.
  ellipsis:  'M6.5 12h.01M12 12h.01M17.5 12h.01',
  // Une horloge : le signe convenu de « ce qui s'est déjà passé ».
  history:   'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM12 8v4.2l2.8 1.8',
  // Une flèche qui reboucle : « il y a du neuf à prendre ». L'arc s'arrête
  // avant de se refermer — un cercle complet se lirait comme un chargement.
  refresh:   'M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4v4h-4',
  // Une cloche, battant compris : sans le petit arc du bas, la forme se lit
  // comme un parapluie à cette taille.
  bell:      'M18 16.5H6s1.5-1.7 1.5-6a4.5 4.5 0 0 1 9 0c0 4.3 1.5 6 1.5 6ZM10.2 19.5a2 2 0 0 0 3.6 0',
  // Un disque et huit rayons courts : le soleil le plus lisible à cette taille
  // reste le plus littéral.
  sun:       'M8.5 12a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0ZM12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4',
  // Un croissant tracé d'un seul contour (deux arcs qui se referment) : sans
  // remplissage, c'est la seule façon de dessiner une lune qui ne soit pas
  // juste un cercle.
  moon:      'M20 13.5A8 8 0 1 1 10.5 4a6.5 6.5 0 0 0 9.5 9.5Z',
  // Un écran et son pied : représente « suit l'appareil », par opposition à
  // un choix forcé (soleil/lune).
  monitor:   'M4 5.5h16v10.5h-16zM9 19.5h6M12 16v3.5',
  // Un simple chevron, distinct de `arrowDown` (qui porte une hampe et sert
  // aux montants) : celui-ci n'indique qu'un dépliant, rien ne « descend ».
  chevronDown: 'M6 9.5l6 6 6-6',
}

export function Icon({ name, size = 22 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
